import React, { useState } from 'react';
import {
  FolderSearch, Check, Clock, AlertTriangle, Image as ImageIcon,
  Mail, Eye, ArrowRight,
} from 'lucide-react';
import { useFileRequestCategories, labelFor, useFileRequestCopy, copyFor }
  from '@/lib/fileRequestCategories';

/**
 * "What's new" — file requests. Editors and admin/staff; no client variant.
 *
 * TWO KINDS OF STRING IN HERE, AND THE DIFFERENCE IS THE WHOLE POINT:
 *
 *   1. PRODUCT COPY — anything the reader can also see in the portal. Imported,
 *      never typed: panel strings via `copyFor()` from the file-request copy map,
 *      category labels via `useFileRequestCategories()`. An announcement whose job
 *      is to show what the panel says must not hold its own copy of what the panel
 *      says; the first time a button is reworded, a hand-typed illustration starts
 *      teaching the wrong thing and nothing fails.
 *
 *   2. ANNOUNCEMENT PROSE — "Ask the client yourself", the numbered rows. This
 *      copy exists only to explain the change and has no other home, so it lives
 *      here next to the component.
 *
 * The test: if the reader could see the string in the product, it is imported.
 *
 * THE AT-RISK LINE USES THE no_eta VARIANT deliberately. An announcement has no
 * project and therefore no delivery date, and substituting a plausible-looking one
 * bakes a date into a modal that will still be showing it months later.
 *
 * ILLUSTRATIONS, NOT SCREENSHOTS. Drawn in markup so they follow the theme, reflow
 * on a phone, and cannot go stale — the same decision, for the same reason, as
 * SongApprovalIntro.
 */

/* ── shared chrome (matches SongApprovalIntro) ─────────────────────────── */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

const Numbered = ({ rows }) => (
  <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
    {rows.map(([t, d], i) => (
      <div key={i} className="flex gap-3">
        <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
        <p className="text-[12px] text-zinc-100/75 leading-relaxed">
          <span className="text-zinc-100 font-medium">{t}.</span> {d}
        </p>
      </div>
    ))}
  </div>
);

/* ── illustrations — every product string imported ─────────────────────── */

const AskIllustration = ({ categories, t }) => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
    <Eyebrow>What you send</Eyebrow>
    <div className="mt-2 grid grid-cols-2 gap-1.5">
      {categories.map((c, i) => (
        <div key={c.value}
             className={`rounded border px-2 py-1.5 text-[10px] leading-tight ${
               i === 2 ? 'bg-ug-gold/10 border-ug-gold/30 text-ug-gold'
                       : 'bg-ug-surface border-ug-border-strong text-zinc-100/70'}`}>
          {labelFor(categories, c.value)}
        </div>
      ))}
    </div>
    <div className="mt-2.5 rounded bg-ug-surface border border-ug-border-strong px-2 py-1.5 text-[11px] text-zinc-100/70">
      {/* Example note — announcement prose, not product copy. */}
      The whole ‘Reception’ folder is missing from the Dropbox.
    </div>
    <div className="mt-2 flex items-center gap-1 text-[10px] text-oxidized-teal">
      <ImageIcon className="w-3 h-3" /> Attach a screenshot
    </div>
  </div>
);

const ClientViewIllustration = ({ t }) => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border">
    {/* NO editor name, avatar or initial anywhere in this block, and none can be
        introduced: nothing here reads from a user record. */}
    <Eyebrow>What the client sees</Eyebrow>
    <div className="mt-2 rounded-lg overflow-hidden border border-ug-border-strong">
      <div className="flex items-center gap-2 bg-ug-gold px-2.5 py-1.5">
        <AlertTriangle className="w-3 h-3 text-ug-deepest" />
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-deepest">
          {t('client_panel.slab')}
        </span>
      </div>
      <div className="p-2.5 bg-ug-surface">
        <p className="text-[10px] text-zinc-100/70 leading-relaxed">
          {t('client_panel.at_risk_no_eta').replace('{at_risk}', 'at risk')}
        </p>
        <div className="mt-2 flex gap-1.5">
          <div className="flex-1 py-1.5 rounded border border-ug-border text-center font-sans text-[9px] font-semibold text-zinc-100/80">
            {t('client_panel.verdict_affirmative')}
          </div>
          <div className="flex-1 py-1.5 rounded border border-ug-border text-center font-sans text-[9px] font-semibold text-zinc-100/80">
            {t('client_panel.verdict_negative')}
          </div>
        </div>
      </div>
    </div>
    <p className="mt-2 text-[10px] text-slate-500 italic">
      They never see who asked — it comes from Uncut Gems.
    </p>
  </div>
);

const AnswersIllustration = ({ t }) => (
  <div className="p-3 rounded-xl bg-ug-surface/50 border border-ug-border space-y-2">
    <Eyebrow>What comes back</Eyebrow>
    <div className="rounded border border-oxidized-teal/40 bg-oxidized-teal/[0.06] px-2.5 py-2">
      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-oxidized-teal block">
        {t('client_panel.verdict_affirmative')}
      </span>
      <p className="text-[10px] text-zinc-100/70 mt-1">The request closes itself. Go and look.</p>
    </div>
    <div className="rounded border border-ug-status-red/40 bg-ug-status-red/[0.06] px-2.5 py-2">
      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-status-red block">
        {t('editor_panel.cannot_provide_title')}
      </span>
      <p className="text-[10px] text-zinc-100/70 mt-1">{t('editor_panel.cannot_provide_next')}</p>
    </div>
    <div className="flex gap-2 pt-0.5">
      <div className="flex-1 py-1.5 rounded-lg border border-ug-border text-slate-300 font-sans text-[9px] font-semibold uppercase tracking-wider text-center">
        {t('editor_panel.action_supersede')}
      </div>
      <div className="flex-1 py-1.5 rounded-lg border border-ug-border text-slate-300 font-sans text-[9px] font-semibold uppercase tracking-wider text-center">
        {t('editor_panel.action_resolve')}
      </div>
    </div>
  </div>
);

/* ── editor: three steps ───────────────────────────────────────────────── */

const editorSteps = (categories, t) => [
  {
    eyebrow: 'Step 1 of 3',
    title: 'Ask the client yourself',
    blurb: 'A missing card, missing audio, a missing folder, or a question about the files — you can now ask the client directly instead of waiting on us.',
    body: <AskIllustration categories={categories} t={t} />,
    rows: [
      ['Pick a category', 'Four to choose from. It sets how the request reads to the client.'],
      ['Write one short note', 'Say what is actually missing. This goes to them word for word.'],
      ['Attach a screenshot', 'Optional, up to three. Often faster than describing a folder tree.'],
    ],
  },
  {
    eyebrow: 'Step 2 of 3',
    title: 'It reaches them immediately',
    blurb: 'The request is emailed the moment you send it. You are not waiting on a project manager to pass it along, at any hour.',
    body: <ClientViewIllustration t={t} />,
    rows: [
      ['Admin is told automatically', 'They stay informed without sitting between you and the client.'],
      ['The client never learns who asked', 'Every client-facing word says “we”. Your name is never attached.'],
    ],
  },
  {
    eyebrow: 'Step 3 of 3',
    title: 'One request, one answer',
    blurb: 'They reply one of two ways: they have uploaded it, or they cannot provide it. Either way you hear back, and either way you are unblocked.',
    body: <AnswersIllustration t={t} />,
    rows: [
      ['A follow-up is a new request', 'Not a reply. Deliberately — so it never becomes a thread you have to manage.'],
      ['Close it or replace it', 'Once they have answered, it is yours to close or swap for what you actually need.'],
    ],
  },
];

/* ── the announcement ──────────────────────────────────────────────────── */

export function FileRequestsAnnouncement({ role, onAcknowledge, embedded = false, forcedStep = null }) {
  const categories = useFileRequestCategories();
  const copy = useFileRequestCopy();
  const [step, setStep] = useState(0);
  const t = (key) => copyFor(copy, key);

  // GATED ON THE CATEGORIES FETCH. `labelFor` degrades to a generic fallback when
  // the list has not arrived, which is right for a panel and wrong here: a modal
  // that opens showing "We need something" four times, then silently corrects
  // itself, teaches nothing and looks broken. Render nothing until it is real.
  if (!categories.length) return null;

  const isEditor = role === 'editor';
  const steps = editorSteps(categories, t);
  const idx = forcedStep ?? step;
  const last = idx === steps.length - 1;

  const inner = isEditor ? (
    <>
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <FolderSearch className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
          <span className="ml-auto font-sans text-[9px] uppercase tracking-wider text-slate-500">{steps[idx].eyebrow}</span>
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">{steps[idx].title}</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">{steps[idx].blurb}</p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30">{steps[idx].body}</div>
      <Numbered rows={steps[idx].rows} />
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20 flex items-center gap-3">
        <div className="flex gap-1.5">
          {steps.map((_, n) => (
            <span key={n} className={`w-1.5 h-1.5 rounded-full ${n === idx ? 'bg-ug-gold' : 'bg-ug-border-strong'}`} />
          ))}
        </div>
        <button
          data-testid="whats-new-next"
          onClick={() => (last ? onAcknowledge && onAcknowledge() : setStep(n => n + 1))}
          className="tap-auto ml-auto px-5 py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5">
          {last ? 'Got it' : 'Next'}
          {!last && <ArrowRight className="w-3 h-3" />}
        </button>
      </div>
    </>
  ) : (
    <>
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <FolderSearch className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Editors can ask clients directly</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          When an editor hits a missing card or an unclear instruction, it now goes straight to the
          client. You are informed, not in the critical path.
        </p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What reaches you</Eyebrow>
        <div className="space-y-1.5">
          {[[Mail, 'Email when a request is raised, and when the client answers'],
            [Eye, 'The full exchange in the project drawer — both sides'],
            [Clock, 'No answer after 48 hours surfaces on your dashboard']].map(([Icon, text], i) => (
            <div key={i} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-zinc-100/75">{text}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-ug-status-red/40 bg-ug-status-red/[0.06] px-3 py-2.5">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-status-red block">
            {t('admin_panel.slab_cannot_provide')}
          </span>
          <p className="text-[11px] text-zinc-100/75 mt-1 leading-relaxed">
            The one outcome no timer resolves. It needs a person.
          </p>
        </div>
      </div>
      <Numbered rows={[
        ['You are informed, not blocking', 'The request goes out without your approval. That is the point — it removes the wait.'],
        ['The 48-hour flag is not pushed', 'It appears when you load the dashboard. Nothing emails you at the 48-hour mark.'],
        ['Close, cancel or replace', 'All three are yours from the project drawer, where the whole exchange is visible.'],
        ['The client never learns which editor asked', 'Every client-facing word says “we”. Names appear only in your copy and the editor’s.'],
      ]} />
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </>
  );

  const shell = (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid={`whats-new-file-requests-${isEditor ? 'editor' : 'admin'}`}>
      {inner}
    </div>
  );

  // `embedded` renders the card alone, for the review route. The wired mount
  // (Phase 2) uses the blocking overlay.
  if (embedded) return shell;
  return (
    <div className="ug-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         role="dialog" aria-modal="true" data-testid="whats-new-modal">
      {shell}
    </div>
  );
}

export default FileRequestsAnnouncement;
